/**
 * useUnits — the React entry point to the display-conversion pipeline.
 *
 * Resolves, once per render:
 *   • the field-units registry (field → quantity class),
 *   • the viewer's *display* profile key — the user's chosen profile, else the
 *     org default, else null (= follow each record's own stored profile),
 *   • the resolved profiles it needs (display + whatever stored profiles appear).
 *
 * and hands back `convertField(table, col, value, storedProfileKey)` →
 * `{ value, unit }` plus small formatting helpers. Everything is memoised so a
 * screen can convert dozens of values per render cheaply.
 */
import { useCallback, useMemo } from "react";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import { intlTag } from "@lib/format";
import {
  useGetFieldUnitsQuery,
  useGetOrgEffectiveQuery,
  useGetResolvedProfileQuery,
} from "./unitsApi";
import { convertField } from "./resolve";
import type { ConvertedValue } from "./types";

/** Fallback stored profile when a record carries none (US-seed default). */
const FALLBACK_PROFILE = "metric";

export interface UseUnits {
  /** True while the reference data is still loading. */
  isLoading: boolean;
  /** The display profile key actually in effect (after Auto resolution). */
  displayProfileKey: string;
  /**
   * Convert one field value from its stored profile to the display profile.
   * `storedProfileKey` is the record's `unit_profile_key` (e.g. `api`/`metric`);
   * null/undefined falls back to the display profile (no conversion).
   */
  convertField: (
    table: string,
    col: string,
    value: number | null | undefined,
    storedProfileKey?: string | null,
  ) => ConvertedValue;
  /** Format a converted value + append its dynamic unit label ("5,623 ft"). */
  fmt: (cv: ConvertedValue, opts?: Intl.NumberFormatOptions) => string;
}

export function useUnits(storedProfileKeyHint?: string | null): UseUnits {
  const { locale } = useLocale();
  const chosen = useAppSelector((s) => s.prefs.displayProfile);

  const { data: fieldMap, isLoading: mapLoading } = useGetFieldUnitsQuery();
  // Org default only matters in Auto mode; it 4xxs when unset (treated as none).
  const { data: orgEffective, isLoading: orgLoading } = useGetOrgEffectiveQuery(undefined, {
    skip: chosen != null,
  });

  // Resolve the display profile key: explicit choice → org default → the record's
  // own stored profile (hint) → the metric fallback.
  const displayProfileKey =
    chosen ?? orgEffective?.profile_key ?? storedProfileKeyHint ?? FALLBACK_PROFILE;

  const { data: displayProfile, isLoading: dispLoading } =
    useGetResolvedProfileQuery(displayProfileKey);

  // The stored profile the current screen most commonly needs (the hint). Other
  // stored profiles resolve lazily via the shared RTK cache on demand.
  const storedKey = storedProfileKeyHint ?? displayProfileKey;
  const { data: storedProfile } = useGetResolvedProfileQuery(storedKey, {
    skip: storedKey === displayProfileKey,
  });

  const doConvert = useCallback<UseUnits["convertField"]>(
    (table, col, value, storedProfileKey) => {
      // Pick the right stored profile: matches the hint (cached above) or, when a
      // row carries a different profile, matches the display side if identical.
      const effectiveStored =
        storedProfileKey == null || storedProfileKey === displayProfileKey
          ? displayProfile
          : storedProfileKey === storedKey
            ? (storedProfile ?? displayProfile)
            : displayProfile; // unknown extra profile → best-effort (no conv)
      return convertField(fieldMap, table, col, value, effectiveStored, displayProfile);
    },
    [fieldMap, displayProfile, storedProfile, storedKey, displayProfileKey],
  );

  const fmt = useCallback<UseUnits["fmt"]>(
    (cv, opts) => {
      if (cv.value == null || !Number.isFinite(cv.value)) return "—";
      const n = cv.value.toLocaleString(intlTag(locale), opts ?? {});
      return cv.unit ? `${n} ${cv.unit}` : n;
    },
    [locale],
  );

  return useMemo(
    () => ({
      isLoading: mapLoading || dispLoading || orgLoading,
      displayProfileKey,
      convertField: doConvert,
      fmt,
    }),
    [mapLoading, dispLoading, orgLoading, displayProfileKey, doConvert, fmt],
  );
}
